import { Helmet } from "react-helmet-async";

export default function SEO({
  title,
  description,
  keywords = [],
  image,
  url,
  type = "website",
  author = "Saloni Shah",
  publishedTime,
  modifiedTime,
  section,
  tags = [],
}) {
  const baseUrl = "https://greyzone-ai.com";
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const defaultImage = "https://images.unsplash.com/photo-1758599543125-0a927f1d7a3b?w=1200";
  const ogImage = image || defaultImage;
  
  const defaultKeywords = [
    "AI governance",
    "SaMD regulations",
    "compliance in medtech",
    "medtech regulations",
    "FDA 510(k)",
    "CE marking",
    "EU AI Act",
    "ISO 13485",
    "PCCP",
    "medical device compliance",
  ];
  
  const allKeywords = [...new Set([...keywords, ...defaultKeywords])];

  // JSON-LD structured data
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Saloni Shah",
    jobTitle: "Lead Compliance Engineer",
    worksFor: {
      "@type": "Organization",
      name: "ResMed",
    },
    url: baseUrl,
    sameAs: ["https://www.linkedin.com/in/saloni-s-246823188"],
    description:
      "Expert in AI governance, SaMD regulations, and MedTech compliance with 8+ years of experience.",
    knowsAbout: [
      "AI Governance",
      "Medical Device Regulations",
      "FDA 510(k)",
      "CE Marking",
      "EU AI Act",
      "ISO 13485",
      "PCCP",
      "SaMD Compliance",
    ],
    alumniOf: {
      "@type": "CollegeOrUniversity",
      name: "State University of New York - Binghamton",
    },
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Saloni Shah - AI Governance & MedTech Compliance",
    url: baseUrl,
    author: personSchema,
    description:
      "Expert insights on AI governance, SaMD regulations, and MedTech compliance.",
  };

  const articleSchema =
    type === "article"
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description: description,
          image: ogImage,
          author: personSchema,
          publisher: {
            "@type": "Person",
            name: "Saloni Shah",
            url: baseUrl,
          },
          datePublished: publishedTime,
          dateModified: modifiedTime || publishedTime,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": fullUrl,
          },
          articleSection: section,
          keywords: tags.join(", "),
        }
      : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords.join(", ")} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Saloni Shah" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />

      {/* Article specific */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}
      {type === "article" &&
        tags.map((tag, i) => (
          <meta property="article:tag" content={tag} key={i} />
        ))}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      {articleSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      )}
    </Helmet>
  );
}
